import { useState } from 'react';

import { classifyHearing, classifyHearingText, colorCodes } from '../utils';

import noDial from '../assets/images/no-difficulty.png';
import severeDial from '../assets/images/severe-difficulty.png';
import significantDial from '../assets/images/significant-difficulty.png';
import someDial from '../assets/images/some-difficulty.png';

import { useTranslation } from 'react-i18next';
import downArrow from '../assets/images/blue_down_arrow.png';
import { Side } from '../routes/detailed-results';
import { DiffLevels, EarResult } from '../types';

const diffLevels: DiffLevels = {
  high: '(2,000 - 8,000 HZ)',
  mid: '(1,000 - 2,000 HZ)',
  low: '(500 - 1,000 HZ)',
};

const dialsMap = {
  severe: severeDial,
  significant: significantDial,
  some: someDial,
  no: noDial,
};

interface FrequencyLabels {
  en: Record<string, string>;
  es: Record<string, string>;
}

// Manually defined translations for frequency labels
const frequencyLabels: FrequencyLabels = {
  en: {
    high: 'High Frequencies',
    mid: 'Mid Frequencies',
    low: 'Low Frequencies',
  },
  es: {
    high: 'Frecuencias Altas',
    mid: 'Frecuencias Medias',
    low: 'Frecuencias Bajas',
  },
};

const EarResults = ({ side, data }: { side: Side; data: EarResult }) => {
  const { t, i18n } = useTranslation('earResults');
  const currentLanguage = i18n.language;

  const [showDetails, setShowDetails] = useState<{
    Left: number | null;
    Right: number | null;
  }>({
    Left: null,
    Right: null,
  });

  const handleShowDetails = (side: Side, index: number) => {
    // use the English key
    if (side === 'Oído izquierdo') {
      side = 'Left';
    }
    if (side === 'Oído derecho') {
      side = 'Right';
    }
    let showDetailsCopy = { ...showDetails };
    showDetailsCopy[side] = showDetailsCopy[side] === index ? null : index;
    setShowDetails(showDetailsCopy);
  };

  const isSectionOpen = (side: Side, index: number) =>
    (side === 'Left' && showDetails['Left'] === index) ||
    (side === 'Right' && showDetails['Right'] === index) ||
    (side === 'Oído izquierdo' && showDetails['Left'] === index) ||
    (side === 'Oído derecho' && showDetails['Right'] === index);

  return (
    <div className="ear-result">
      <div className="ear-result-title">
        <h3>{t('ear-title', { side })}</h3>
      </div>
      <div className="left-right">
        <div className="left">
          <img
            src={dialsMap[classifyHearing(data.overall)]}
            alt="semicircle dial"
          />
          <div className="left-text">
            <h3>{t('ear-score', { side })}</h3>
            <p>{t('summary-text')}</p>
          </div>
        </div>

        <div className="right">
          {Object.keys(diffLevels).map((level, i) => (
            <div className="frequency-info" key={`freq-${i}`}>
              <div style={{ display: 'flex' }}>
                <span style={{ marginBottom: '12px' }}>
                  <span style={{ fontFamily: 'Noto Sans Bold' }}>
                    {
                      frequencyLabels[currentLanguage as keyof FrequencyLabels][
                        level
                      ]
                    }
                  </span>
                  {` ${diffLevels[level as keyof DiffLevels]}`} -
                  <span
                    style={{
                      color: `${colorCodes[classifyHearing(data[level as keyof EarResult])]}`,
                      marginLeft: '5px',
                      fontFamily: 'Noto Sans Bold',
                    }}
                  >
                    {classifyHearingText(
                      data[level as keyof EarResult],
                      currentLanguage,
                    ).toUpperCase()}
                  </span>
                </span>
              </div>

              {isSectionOpen(side, i) && (
                <div className="more-details">
                  <img
                    src={
                      dialsMap[classifyHearing(data[level as keyof EarResult])]
                    }
                    alt="semicircle dial"
                  />
                  <p>{t(`explanations.${level}.description`)}</p>
                </div>
              )}

              <button
                className="details-button"
                onClick={() => handleShowDetails(side, i)}
              >
                <p>
                  {t(isSectionOpen(side, i) ? 'less-details' : 'more-details')}
                </p>
                <img
                  src={downArrow}
                  alt={t('blue-arrow-alt')}
                  style={
                    isSectionOpen(side, i)
                      ? { transform: 'rotate(180deg)' }
                      : {}
                  }
                />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EarResults;
